import { ReactNode } from "react";

import { MULTISIG_ORG_STATUS, MULTISIG_SIGNER_ROLE } from "@/constants/multisigEnrollment";
import {
  TERMINAL_UNENROLLMENT_STATUSES,
  UNENROLLMENT_TX_STATUS,
} from "@/constants/multisigUnenrollment";

import { activeOwnerSigners, findMySigner, toActiveSigners } from "@/helpers/multisigSigners";

import type {
  ApiOrganizationMultisig,
  ApiUser,
  MultisigActiveSigner,
  MultisigUnenrollmentStatus,
  MultisigUnenrollmentTransaction,
  MultisigUnenrollmentTxStatus,
  UserRole,
} from "@/types";

const UNENROLLMENT_ELIGIBLE_ROLES: ReadonlyArray<UserRole> = ["owner"];

const MIN_ACTIVE_OWNER_SIGNERS_FOR_UNENROLLMENT = 2;

export type UnenrollmentUnavailableReason = "not_owner" | "not_enrolled" | "quorum_unreachable";

export type UnenrollmentInProgressStance =
  | "awaiting-my-signature"
  | "awaiting-other-owner"
  | "submitted";

export type UnenrollmentMe = {
  userId: string;
  isOwner: boolean;
  signer: MultisigActiveSigner | null;
};

export type UnenrollmentSignRequest = {
  txId: string;
  unsignedXdr: string;
};

type UnenrollmentViewBase = {
  activeSigners: MultisigActiveSigner[];
  activeOwnerCount: number;
  me: UnenrollmentMe;
};

export type UnenrollmentView =
  | (UnenrollmentViewBase & { kind: "unavailable"; reason: UnenrollmentUnavailableReason })
  | (UnenrollmentViewBase & { kind: "eligible" })
  | (UnenrollmentViewBase & {
      kind: "in-progress";
      tx: MultisigUnenrollmentTransaction;
      stance: UnenrollmentInProgressStance;
      awaitingCount: number;
      statusLabel: string;
      hasMySignature: boolean;
      canSign: boolean;
      unsignedXdr: string;
      signRequest: UnenrollmentSignRequest | null;
      /** Who started the removal, resolved against the roster; `null` when unknown or gone. */
      initiator: MultisigActiveSigner | null;
      initiatorUserId: string | null;
      isInitiator: boolean;
      expiresAt: string | null;
      /** The XDR's timebounds have lapsed; a signature would now be refused. */
      isExpired: boolean;
      /** Active Owner signers other than the caller. */
      otherOwners: MultisigActiveSigner[];
      /** `otherOwners` who have not signed yet: the people being waited on. */
      pendingOwners: MultisigActiveSigner[];
    })
  | (UnenrollmentViewBase & { kind: "completed"; tx: MultisigUnenrollmentStatus })
  | (UnenrollmentViewBase & { kind: "expired"; tx: MultisigUnenrollmentStatus })
  | (UnenrollmentViewBase & { kind: "failed"; tx: MultisigUnenrollmentStatus })
  | (UnenrollmentViewBase & { kind: "cancelled"; tx: MultisigUnenrollmentStatus });

export type UnenrollmentTerminalKind = "completed" | "expired" | "failed" | "cancelled";

export const isUnenrollmentEligible = (role: UserRole | null | undefined): boolean =>
  !!role && UNENROLLMENT_ELIGIBLE_ROLES.includes(role);

export const isTerminalUnenrollmentStatus = (
  status: MultisigUnenrollmentTxStatus | undefined | null,
): boolean => !!status && TERMINAL_UNENROLLMENT_STATUSES.includes(status);

export const unenrollmentStatusLabel = (status: MultisigUnenrollmentTxStatus): string => {
  switch (status) {
    case UNENROLLMENT_TX_STATUS.PENDING_SIGNATURES:
      return "Waiting for signatures";
    case UNENROLLMENT_TX_STATUS.READY_TO_SUBMIT:
      return "Submitting to the Stellar network";
    case UNENROLLMENT_TX_STATUS.SUBMITTED:
      return "Submitted, waiting for confirmation";
    case UNENROLLMENT_TX_STATUS.CONFIRMED:
      return "Confirmed on the Stellar network";
    case UNENROLLMENT_TX_STATUS.EXPIRED:
      return "Expired";
    case UNENROLLMENT_TX_STATUS.FAILED:
      return "Failed";
    case UNENROLLMENT_TX_STATUS.CANCELLED:
      return "Cancelled";
  }
};

const terminalKindFor = (status: MultisigUnenrollmentTxStatus): UnenrollmentTerminalKind | null => {
  switch (status) {
    case UNENROLLMENT_TX_STATUS.CONFIRMED:
      return "completed";
    case UNENROLLMENT_TX_STATUS.EXPIRED:
      return "expired";
    case UNENROLLMENT_TX_STATUS.FAILED:
      return "failed";
    case UNENROLLMENT_TX_STATUS.CANCELLED:
      return "cancelled";
    default:
      return null;
  }
};

export const formatSignerName = (
  user: { first_name?: string | null; last_name?: string | null } | null | undefined,
  fallback = "an Owner",
): string => {
  if (!user) return fallback;
  const name = `${user.first_name ?? ""} ${user.last_name ?? ""}`.replace(/\s+/g, " ").trim();
  return name || fallback;
};

export const formatActiveSignerName = (
  signer: MultisigActiveSigner | null | undefined,
  fallback?: string,
): string => formatSignerName(signer, fallback);

/**
 * Whether one of the collected signatures belongs to the given person. A
 * signature carries the signer's user id when the key still maps to an
 * active signer row, and only the public key otherwise, so both are checked.
 */
export const hasSignatureFrom = (
  tx: Pick<MultisigUnenrollmentTransaction, "signatures">,
  who: { userId?: string | null; signerKey?: string | null },
): boolean =>
  (tx.signatures ?? []).some(
    (s) =>
      (!!who.userId && s.signer_user_id === who.userId) ||
      (!!who.signerKey && s.signer_public_key === who.signerKey),
  );

/** Whether the removal's XDR timebounds have lapsed. Unknown or malformed `expires_at` is not expired. */
export const isUnenrollmentExpired = (
  tx: Pick<MultisigUnenrollmentTransaction, "expires_at">,
  now: Date = new Date(),
): boolean => {
  if (!tx.expires_at) return false;
  const expiresAt = Date.parse(tx.expires_at);
  return !Number.isNaN(expiresAt) && expiresAt <= now.getTime();
};

export type DeriveUnenrollmentViewInput = {
  org: ApiOrganizationMultisig | undefined;
  users: ApiUser[] | undefined;
  /** What `GET /multisig/unenrollment` returned: the active removal, or `null` (404). */
  tx: MultisigUnenrollmentTransaction | null | undefined;
  /**
   * The last status a mutation returned. Finished removals vanish from the
   * GET, so this is what keeps the terminal screen reachable after that.
   */
  lastKnownTx?: MultisigUnenrollmentStatus | null;
  userId: string;
  role: UserRole | null;
  /** Injectable clock for the expiry check. */
  now?: Date;
};

export const deriveUnenrollmentView = ({
  org,
  users,
  tx,
  lastKnownTx,
  userId,
  role,
  now = new Date(),
}: DeriveUnenrollmentViewInput): UnenrollmentView => {
  const activeSigners = toActiveSigners(users);
  const owners = activeOwnerSigners(activeSigners);
  const me: UnenrollmentMe = {
    userId,
    isOwner: isUnenrollmentEligible(role),
    signer: findMySigner(activeSigners, userId),
  };
  const base: UnenrollmentViewBase = { activeSigners, activeOwnerCount: owners.length, me };

  if (!me.isOwner) {
    return { ...base, kind: "unavailable", reason: "not_owner" };
  }

  if (tx) {
    const terminal = terminalKindFor(tx.status);
    if (terminal) return { ...base, kind: terminal, tx };

    const awaitingCount = Math.max(0, tx.required_signatures - tx.collected_signatures);
    const collectionDone =
      awaitingCount === 0 ||
      tx.status === UNENROLLMENT_TX_STATUS.READY_TO_SUBMIT ||
      tx.status === UNENROLLMENT_TX_STATUS.SUBMITTED;

    const signed = hasSignatureFrom(tx, { userId, signerKey: me.signer?.stellar_public_key });
    const isExpired = isUnenrollmentExpired(tx, now);
    const isOwnerSigner = me.signer?.role === MULTISIG_SIGNER_ROLE.OWNER;
    const canSign = !collectionDone && isOwnerSigner && !signed && !isExpired;

    let stance: UnenrollmentInProgressStance;
    if (collectionDone) stance = "submitted";
    else if (canSign) stance = "awaiting-my-signature";
    else stance = "awaiting-other-owner";

    const initiatorUserId = tx.initiator_user_id ?? null;
    const otherOwners = owners.filter((owner) => owner.user_id !== userId);

    return {
      ...base,
      kind: "in-progress",
      tx,
      stance,
      awaitingCount,
      statusLabel: unenrollmentStatusLabel(tx.status),
      hasMySignature: signed,
      canSign,
      unsignedXdr: tx.unsigned_xdr,
      signRequest: canSign ? { txId: tx.tx_id, unsignedXdr: tx.unsigned_xdr } : null,
      initiator: findMySigner(activeSigners, initiatorUserId),
      initiatorUserId,
      isInitiator: !!initiatorUserId && initiatorUserId === userId,
      expiresAt: tx.expires_at ?? null,
      isExpired,
      otherOwners,
      pendingOwners: otherOwners.filter(
        (owner) =>
          !hasSignatureFrom(tx, { userId: owner.user_id, signerKey: owner.stellar_public_key }),
      ),
    };
  }

  const isEnrolled = org?.multisig_status === MULTISIG_ORG_STATUS.ENROLLED;

  if (lastKnownTx) {
    const terminal = terminalKindFor(lastKnownTx.status);
    if (terminal) return { ...base, kind: terminal, tx: lastKnownTx };

    // The GET stopped serving the removal we were tracking and the org is no
    // longer enrolled: the only path there is a confirmed unenrollment.
    const wasPastSigning =
      lastKnownTx.status === UNENROLLMENT_TX_STATUS.READY_TO_SUBMIT ||
      lastKnownTx.status === UNENROLLMENT_TX_STATUS.SUBMITTED;
    if (wasPastSigning && !isEnrolled) {
      return {
        ...base,
        kind: "completed",
        tx: { ...lastKnownTx, status: UNENROLLMENT_TX_STATUS.CONFIRMED },
      };
    }
    // Otherwise someone else cancelled it, or it lapsed and was flipped to
    // EXPIRED. Fall through to the preflight so the Owner can start again.
  }

  if (!isEnrolled) {
    return { ...base, kind: "unavailable", reason: "not_enrolled" };
  }
  if (owners.length < MIN_ACTIVE_OWNER_SIGNERS_FOR_UNENROLLMENT) {
    return { ...base, kind: "unavailable", reason: "quorum_unreachable" };
  }
  return { ...base, kind: "eligible" };
};

const pendingOwnerName = (view: Extract<UnenrollmentView, { kind: "in-progress" }>): string =>
  view.pendingOwners.length === 1 ? formatActiveSignerName(view.pendingOwners[0]) : "another Owner";

export const unenrollmentSignatureNote = (
  view: Extract<UnenrollmentView, { kind: "in-progress" }>,
): ReactNode => {
  if (view.isExpired && view.stance !== "submitted") {
    return <>This removal expired before both Owners signed. Start a new one.</>;
  }
  switch (view.stance) {
    case "awaiting-my-signature":
      return (
        <>Sign the removal in VIA Wallet to keep going. {view.awaitingCount} signature(s) left.</>
      );
    case "awaiting-other-owner":
      return <>Waiting for {pendingOwnerName(view)} to sign the removal in VIA Wallet.</>;
    case "submitted":
      return <>Both signatures collected. Waiting for the Stellar network to confirm.</>;
  }
};
